import { forwardRef, type InputHTMLAttributes, type LabelHTMLAttributes, type ReactNode } from "react";

import { cn } from "@/lib/cn";
import { mobileInput } from "@/lib/phone";

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...props }, ref) {
    return (
      <input
        ref={ref}
        className={cn(
          "h-10 w-full rounded-lg border border-border bg-surface px-3 text-sm text-fg",
          "placeholder:text-subtle transition-colors",
          "focus-visible:border-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/25",
          "aria-invalid:border-down disabled:opacity-60",
          className,
        )}
        {...props}
      />
    );
  },
);

export function Label({ className, ...props }: LabelHTMLAttributes<HTMLLabelElement>) {
  return <label className={cn("text-xs font-medium text-muted", className)} {...props} />;
}

/** Label + control + optional hint/error, stacked. */
export function Field({
  label,
  htmlFor,
  hint,
  error,
  children,
}: {
  label: ReactNode;
  htmlFor: string;
  hint?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {error ? (
        <p className="text-xs text-down" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className="text-xs text-subtle">{hint}</p>
      ) : null}
    </div>
  );
}

/**
 * An Indian mobile number: a fixed "+91" in front, digits only (a pasted
 * "+91 98765 43210" or "098765 43210" keeps just the 10 digits).
 */
export const MobileInput = forwardRef<
  HTMLInputElement,
  Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange"> & {
    value: string;
    onValueChange: (digits: string) => void;
  }
>(function MobileInput({ className, value, onValueChange, ...props }, ref) {
  return (
    <div
      className={cn(
        "flex h-10 w-full items-stretch overflow-hidden rounded-lg border border-border bg-surface text-sm text-fg transition-colors",
        "focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/25",
        "has-[input[aria-invalid=true]]:border-down",
        className,
      )}
    >
      <span aria-hidden className="num flex items-center border-r border-border bg-surface-2 px-3 text-muted select-none">
        +91
      </span>
      <input
        ref={ref}
        type="tel"
        inputMode="numeric"
        autoComplete="tel-national"
        placeholder="98765 43210"
        className="num min-w-0 flex-1 bg-transparent px-3 placeholder:text-subtle focus-visible:outline-none disabled:opacity-60"
        value={value}
        onChange={(e) => onValueChange(mobileInput(e.target.value))}
        {...props}
      />
    </div>
  );
});
